import { SpecializationDto, TherapistAvailabilityDto, TherapistDto } from '../../../../../shared/dtos/therapist.dto';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Image, Row, Spinner, Stack } from 'react-bootstrap';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import i18next from 'i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import Form from 'react-bootstrap/Form';
import { Typeahead } from 'react-bootstrap-typeahead';
import { TreatmentContractDto } from '../../../../../shared/dtos/treatmentContract.dto';
import LexicalEditor from '../../textEditor/LexicalEditor';
import AvatarModal from '../../AvatarModal';
import AvailabilityTable from '../AvailabilityTable';

const BOOKING_BEFORE_OPTIONS: number[] = [4, 8, 12, 24, 48, 72] as const;
type BookingBefore = (typeof BOOKING_BEFORE_OPTIONS)[number];

type TherapistSettingsProps = {
  userProfile: TherapistDto,
  onSubmitSuccess?: (updated: TherapistDto) => void,
  onSubmitError?: (error: unknown) => void,
}

type FormValues = {
  about: string;
  privacyPolicy: string;
  termsAndCondition: string;
  bookingBefore: BookingBefore;
  availability: TherapistAvailabilityDto;
  specializations: SpecializationDto[];
  profilePic?: File | null;
}

function createEmptyAvailability(): TherapistAvailabilityDto {
  return {
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: [],
  };
}
const TherapistSettings: FC<TherapistSettingsProps> = ({ userProfile, onSubmitSuccess, onSubmitError }: TherapistSettingsProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const userLanguage: string = i18next.language;

  const [showAvatarModal, setShowAvatarModal] = useState(false);

  const [loadingSpecializations, setLoadingSpecializations] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);
  const isLoading: boolean = loadingSpecializations || loadingContract;

  const [specializations, setSpecializations] = useState<SpecializationDto[]>([]);
  const [contract, setContract] = useState<TreatmentContractDto | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const fetchSpecializations = useCallback(async () => {
    setLoadingSpecializations(true);

    try {
      const result: SpecializationDto[] = await authFetch('/api/v1/specialization', { method: 'GET' });
      setSpecializations(result);

    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSpecializations(false);
    }
  }, [authFetch]);

  const fetchContract = useCallback(async () => {
    setLoadingContract(true);

    try {
      const result: TreatmentContractDto = await authFetch(`/api/v1/therapist/contract/${userProfile._id}`, { method: 'GET' });
      setContract(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingContract(false);
    }
  }, [authFetch, userProfile]);

  useEffect(() => {
    void fetchSpecializations();
    void fetchContract();
  }, [fetchSpecializations, fetchContract]);

  const selectedSpecializations = (all: SpecializationDto[], ids: string[]): SpecializationDto[] => {
    if (!ids.length) {
      return [];
    }

    const idSet: Set<string> = new Set(ids.map((id: string) => id));
    return all.filter((specialization: SpecializationDto) => idSet.has(specialization._id));
  };

  const initialValues: FormValues = useMemo(() => ({
    about: userProfile.about ?? '',
    privacyPolicy: contract?.privacyPolicy || i18next.t('treatmentContractDefaults.privacyPolicy'),
    termsAndCondition: contract?.termsAndConditions || i18next.t('treatmentContractDefaults.termsAndCondition'),
    bookingBefore: userProfile.bookingBefore ?? 4,
    availability: userProfile.availability ?? createEmptyAvailability(),
    specializations: selectedSpecializations(specializations, userProfile.specialization ?? []),
    profilePic: null,
  }), [userProfile, contract, specializations]);

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setIsSaving(true);
    setSubmitting(true);

    try {
      const data: FormData = new FormData();
      data.append('specialization', JSON.stringify(values.specializations.map((specialization) => specialization._id)));
      data.append('availability', JSON.stringify(values.availability));
      data.append('about', values.about.trim());
      data.append('privacyPolicy', values.privacyPolicy.trim());
      data.append('termsAndCondition', values.termsAndCondition.trim());
      data.append('bookingBefore', values.bookingBefore.toString());

      if (values.profilePic) {
        data.append('profilePic', values.profilePic, values.profilePic.name);
      }

      const updated: TherapistDto = await authFetch('/api/v1/therapist/update', { method: 'PATCH', body: data });
      onSubmitSuccess?.(updated);
    } catch (error) {
      console.error(error);
      onSubmitError?.(error);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const currentAvatarSrc: string = avatarPreview ?? userProfile.profilePic ?? '../default_profile.png';

  return (
    <div className="position-relative pt-3 w-100">
      {(isLoading || isSaving) && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 2 }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1.0 }}
          >
            <AvatarModal
              isShow={showAvatarModal}
              src={userProfile.profilePic ?? null}
              onPositive={(file: File, dataUrl: string) => {
                setFieldValue('profilePic', file);
                setAvatarPreview(dataUrl);
                setShowAvatarModal(false);
              }}
              onNegative={() => setShowAvatarModal(false)}
            />

            <Stack gap={4}>
              <section>
                <h2 className="fw-semibold mb-3">
                  {i18next.t('therapistSettings.title')}
                </h2>
                <p className="text-muted">
                  {i18next.t('therapistSettings.description')}
                </p>
                <h2 className="fw-semibold mb-3">
                  {i18next.t('therapistSettings.aboutYou')}
                </h2>
                <p className="text-muted">
                  {i18next.t('therapistSettings.aboutYouDescription')}
                </p>

                <Row className="pb-3">
                  <Col md={6}>
                    <Image
                      src={currentAvatarSrc}
                      roundedCircle
                      className="border"
                      width="80"
                      height="80"
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      className="ms-4 text-nowrap"
                      onClick={() => setShowAvatarModal(true)}
                    >
                      <i className="fas fa-upload" />{' '}
                      {i18next.t('therapistSettings.selectAvatar')}
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group controlId="about" className="mb-3">
                      <Form.Label>{i18next.t('therapistSettings.about')}</Form.Label>
                      <Form.Label className="float-end">{values.about ? values.about.length : 0}/200</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="about"
                        value={values.about}
                        onChange={(event) => {
                          const newValue: string = event.target.value.length <= 200 ? event.target.value : event.target.value.slice(0, 200);
                          setFieldValue('about', newValue);
                        }}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label> {i18next.t('therapistSettings.specializations')} ({values.specializations.length}/3)</Form.Label>
                      <Typeahead
                        id="specializations-typ"
                        multiple
                        className="pb-2"
                        onChange={(selected) => setFieldValue('specializations', selected)}
                        selected={values.specializations}
                        labelKey={(option: any) => (userLanguage === 'de' ? option.de : option.en)}
                        options={values.specializations.length >= 3 ? [] : specializations}
                        placeholder={i18next.t('therapistSettings.specializations')}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{i18next.t('therapistSettings.bookingBefore')}</Form.Label>
                      <Form.Select
                        name="bookingBefore"
                        className="select-xl"
                        value={values.bookingBefore}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {BOOKING_BEFORE_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour} {i18next.t('generic.hourShort')}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section>
                <AvailabilityTable
                  availability={values.availability}
                  onChange={(next) => setFieldValue('availability', next)}
                />
              </section>

              <section>
                <h2 className="fw-semibold mb-3">
                  {i18next.t('therapistSettings.contract')}
                </h2>
                <p className="text-muted">
                  {i18next.t('therapistSettings.contractDescription')}
                </p>

                <Row>
                  <Col md={6}>
                    <Form.Group controlId="privacyPolicy" className="mb-3">
                      <Form.Label>{i18next.t('therapistSettings.privacyPolicy')}</Form.Label>
                      <LexicalEditor
                        key={'privacy-policy'}
                        value={values.privacyPolicy}
                        placeholder={i18next.t('therapistSettings.privacyPolicy')}
                        onChange={(value) => setFieldValue('privacyPolicy', value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="termsAndConditions" className="mb-3">
                      <Form.Label>{i18next.t('therapistSettings.termsAndCondition')}</Form.Label>
                      <LexicalEditor
                        key={'terms-and-conditions'}
                        value={values.termsAndCondition}
                        placeholder={i18next.t('therapistSettings.termsAndCondition')}
                        onChange={(value) => setFieldValue('termsAndCondition', value)}
                      />
                    </Form.Group>

                  </Col>
                </Row>
              </section>

              <section>
                <div className="d-flex justify-content-start">
                  <Button variant="primary" type="submit" disabled={isSubmitting || isSaving}>
                    {i18next.t('generic.save')}
                  </Button>
                </div>
              </section>

            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TherapistSettings;
